import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import GraphViewer from "../../components/GraphPlotter/GraphViewer";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";
import MaturaPiecewiseInequalityExplanationGraph from "../../generators/dzial3_funkcje/temat6_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/MaturaPiecewiseInequalityExplanationGraph";

const STATIC_GRAPH = {
  points: {
    A: { x: -4, y: 2, markerSymbol: "circle-filled", showLabel: false },
    B: { x: -1, y: -2, showDot: false, showMarker: false, showLabel: false },
    C: { x: 2, y: 3, showDot: false, showMarker: false, showLabel: false },
    D: { x: 5, y: 1, markerSymbol: "circle-filled", showLabel: false },
  },
  segments: [{ p1: "A", p2: "B" }, { p1: "B", p2: "C" }, { p1: "C", p2: "D" }],
  xRange: [-6, 6],
  yRange: [-6, 6],
};

const INEQUALITY_GRAPH = {
  points: {
    A: { x: -4, y: 3, markerSymbol: "circle-filled", showLabel: false },
    B: { x: 0, y: -1, showDot: false, showMarker: false, showLabel: false },
    C: { x: 4, y: 3, markerSymbol: "circle-filled", showLabel: false },
  },
  segments: [{ p1: "A", p2: "B" }, { p1: "B", p2: "C" }],
  xRange: [-6, 6],
  yRange: [-6, 6],
};

const NUMBER_INEQUALITY_INTERVALS = [
  { left: -4, right: -2, leftClosed: true, rightClosed: false },
  { left: 2, right: 4, leftClosed: false, rightClosed: true },
];

const FUNCTION_INEQUALITY_INTERVALS = [
  { left: -4, right: -1, leftClosed: true, rightClosed: false },
  { left: 1, right: 4, leftClosed: false, rightClosed: true },
];

function StaticGraph() {
  return (
    <div style={{ width: "min(100%, 360px)", margin: "10px auto" }}>
      <GraphViewer width={360} {...STATIC_GRAPH} />
    </div>
  );
}

function InequalityGraph({ threshold, solutionIntervals }) {
  return (
    <div style={{ width: "min(100%, 620px)", margin: "10px auto" }}>
      <MaturaPiecewiseInequalityExplanationGraph
        {...INEQUALITY_GRAPH}
        solutionIntervals={solutionIntervals}
        threshold={threshold}
        operator=">"
      />
    </div>
  );
}

export default function FunctionGraphPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        Wykres funkcji składa się z punktów <InlineMath math="(x,f(x))" />.{" "}
        Argument odczytujemy na osi <InlineMath math="OX" />, a odpowiadającą mu
        wartość funkcji na osi <InlineMath math="OY" />.
      </p>
      <p>
        <strong>Dziedzina</strong> to wszystkie argumenty, dla których istnieje punkt
        wykresu. <strong>Zbiór wartości</strong> to wszystkie wysokości osiągane przez wykres.
      </p>
      <div style={{ display: "grid", gap: 8, justifyContent: "center" }}>
        <div><InlineMath math="D_f" /> — rzut wykresu na oś <InlineMath math="OX" /></div>
        <div><InlineMath math="ZW_f" /> — rzut wykresu na oś <InlineMath math="OY" /></div>
      </div>
      <p>
        Pełne kółko oznacza, że punkt należy do wykresu, a puste — że nie należy.
        Ma to wpływ na domknięcie końca przedziału.
      </p>
      <p>
        Zapis <InlineMath math="f(a)" /> oznacza wartość funkcji dla argumentu{" "}
        <InlineMath math="a" />. Równania i nierówności z <InlineMath math="f(x)" />
        pytają natomiast o <strong>argumenty</strong>, więc wynik odczytujemy z osi{" "}
        <InlineMath math="OX" />.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Dziedzina",
      description: "Patrzymy, dla jakich argumentów wykres istnieje.",
      content: (
        <div className="space-y-3">
          <StaticGraph />
          <p>
            Wykres zaczyna się przy <InlineMath math="x=-4" /> i kończy przy{" "}
            <InlineMath math="x=5" />. Oba skrajne punkty są zaznaczone pełnymi kółkami,
            dlatego oba końce należą do dziedziny.
          </p>
          <BlockMath math="D_f=[-4,5]" />
        </div>
      ),
    },
    {
      label: "Przykład 2",
      summary: "Zbiór wartości",
      description: "Patrzymy, jakie wysokości osiąga ten sam wykres.",
      content: (
        <div className="space-y-3">
          <StaticGraph />
          <p>
            Najniższy punkt wykresu ma wartość <InlineMath math="-2" />, a najwyższy{" "}
            <InlineMath math="3" />. Wykres osiąga wszystkie wartości pomiędzy nimi.
          </p>
          <BlockMath math="ZW_f=[-2,3]" />
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Wartość funkcji i argument",
      description: "Przy f(a) zaczynamy od osi OX, a przy f(x)=c — od osi OY.",
      content: (
        <div className="space-y-3">
          <StaticGraph />
          <p>
            Aby odczytać <InlineMath math="f(2)" />, znajdujemy <InlineMath math="x=2" />
            na osi <InlineMath math="OX" /> i idziemy pionowo do wykresu:
          </p>
          <BlockMath math="f(2)=3" />
          <p>
            W równaniu <InlineMath math="f(x)=3" /> zaczynamy od poziomu{" "}
            <InlineMath math="y=3" />. Wykres osiąga tę wysokość dla argumentu{" "}
            <InlineMath math="x=2" />.
          </p>
        </div>
      ),
    },
    {
      label: "Przykład 4",
      summary: "Monotoniczność i maksimum w przedziale",
      description: "Odczytujemy kierunek zmian i ograniczamy uwagę do wskazanego przedziału.",
      content: (
        <div className="space-y-3">
          <StaticGraph />
          <p>
            Na przedziale <InlineMath math="[-4,-1]" /> funkcja jest malejąca, a na{" "}
            <InlineMath math="[-1,2]" /> rosnąca. Dalej, na <InlineMath math="[2,5]" />,
            ponownie jest malejąca.
          </p>
          <p>
            W przedziale <InlineMath math="[0,5]" /> najwyższy punkt ma wysokość{" "}
            <InlineMath math="3" />. Największa <strong>wartość</strong> funkcji wynosi więc{" "}
            <InlineMath math="3" /> — nie mylimy jej z argumentem <InlineMath math="2" />,
            dla którego jest osiągana.
          </p>
        </div>
      ),
    },
    {
      label: "Przykład 5",
      summary: "Nierówności z wykresu",
      description: "Porównujemy wysokość wykresu najpierw z liczbą, a potem z wartością f(a).",
      content: (
        <div className="space-y-3">
          <p><strong>Względem liczby:</strong> rozwiążmy <InlineMath math="f(x)>1" />.</p>
          <InequalityGraph threshold={1} solutionIntervals={NUMBER_INEQUALITY_INTERVALS} />
          <p>
            Wybieramy fragmenty wykresu powyżej przerywanej prostej{" "}
            <InlineMath math="y=1" /> i odczytujemy ich argumenty:
          </p>
          <BlockMath math="x\\in[-4,-2)\\cup(2,4]" />
          <p>
            <strong>Względem wartości funkcji:</strong> rozwiążmy{" "}
            <InlineMath math="f(x)>f(1)" />. Najpierw odczytujemy{" "}
            <InlineMath math="f(1)=0" />, więc rozwiązujemy <InlineMath math="f(x)>0" />.
          </p>
          <InequalityGraph threshold={0} solutionIntervals={FUNCTION_INEQUALITY_INTERVALS} />
          <BlockMath math="x\\in[-4,-1)\\cup(1,4]" />
          <p>
            Porównanie z zerem opisuje <strong>znak funkcji</strong>:{" "}
            <InlineMath math="f(x)>0" /> oznacza wartości dodatnie,{" "}
            <InlineMath math="f(x)<0" /> — ujemne,{" "}
            <InlineMath math="f(x)\\ge 0" /> — nieujemne, a{" "}
            <InlineMath math="f(x)\\le 0" /> — niedodatnie. Przy znakach{" "}
            <InlineMath math="\\ge" /> i <InlineMath math="\\le" /> do rozwiązania
            włączamy także miejsca zerowe.
          </p>
          <p>
            Ponieważ nierówności są ostre, punktów leżących dokładnie na przerywanej
            prostej nie włączamy do rozwiązania.
          </p>
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
